import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Tabs,
  Clipped,
  Text,
  Logo,
  Price,
  RadioGroup,
  Select,
  Columns,
  Heading,
  Image,
} from '@deseretbook/react-ui'
import { toTitleCase } from '@deseretbook/utils'
import { withCheckout } from './CheckoutContext'
import Address from './Address'
import { Link } from '../core'

const IN_STORE_PICKUP = 'pickup'
const DELIVERY = 'ship'

/**
 * Provides information and actions related to a shipment as part of the checkout process.
 */
class ShipmentPanel extends Component {
  /**
   * Component constructor.
   * @param {object} props - Initial component props
   */
  constructor(props) {
    super(props)

    this.state = {
      previousRetailStoreId: null,
    }

    this.shipToStoreRate = props.shipment.shippingRates.find(
      ({ adminName }) => adminName === 'Store Pickup',
    )

    this.selectDeliveryType = this.selectDeliveryType.bind(this)
    this.updateShippingFormField = this.updateShippingFormField.bind(this)
    this.getShippingMethodById = this.getShippingMethodById.bind(this)
    this.renderShippingMethods = this.renderShippingMethods.bind(this)
    this.renderShipToStoreMethod = this.renderShipToStoreMethod.bind(this)
  }

  /**
   * Determines changes to state values based on prop values.
   * @param {object} props - Component props
   * @param {object} state  - Current component state
   * @returns {object} Changes to make to state
   */
  static getDerivedStateFromProps(props, state) {
    if (
      props.form &&
      props.form.retailStoreId &&
      props.form.retailStoreId !== ' ' &&
      props.form.retailStoreId !== state.previousRetailStoreId
    ) {
      return {
        previousRetailStoreId: props.form.retailStoreId,
      }
    }

    return null
  }

  /**
   * Finds and returns a shipping method based on the given shipping method id.
   * @param {number} methodId - Id of requested shipping method
   * @returns {object} - Requested shipping method, or empty object if not found.
   */
  getShippingMethodById(methodId) {
    const { shipment } = this.props

    return shipment.shippingMethods.find(({ id }) => id === methodId) || {}
  }

  /**
   * Selects the specified delivery type. Auto-picks the most relevant delivery method based on
   * the new type.
   *
   * @param {string} type - Type of delivery. Options: `DELIVERY`, `IN_STORE_PICKUP`
   */
  selectDeliveryType(type = DELIVERY) {
    const { shipment, onShipmentChange, isLoading } = this.props
    const { previousRetailStoreId } = this.state

    if (type === IN_STORE_PICKUP && this.shipToStoreRate) {
      onShipmentChange({
        selectedShippingRateId: this.shipToStoreRate.id,
        retailStoreId: previousRetailStoreId,
      })

      return
    }

    const fallbackMethod = shipment.shippingMethods.filter(
      ({ adminName, availableToUsers }) =>
        availableToUsers && adminName !== 'Store Pickup',
    )[0]
    const fallbackRate = shipment.shippingRates.filter(
      ({ shippingMethodId }) =>
        fallbackMethod && shippingMethodId === fallbackMethod.id,
    )[0]

    if (!fallbackRate) {
      return
    }

    onShipmentChange({
      selectedShippingRateId: fallbackRate.id,
      retailStoreId: ' ',
    })
  }

  /**
   * Handles shipping form input events. Applies input value changes.
   *
   * @param {event} event - Input change event
   */
  updateShippingFormField({ target }) {
    const { onShipmentChange } = this.props

    onShipmentChange({ [target.name]: +target.value })
  }

  /**
   * Render helper. Determines how to render the shipping method input field.
   *
   * @returns {function} Component
   */
  renderShippingMethods() {
    const { form, shipment } = this.props
    const isShipToStore =
      this.shipToStoreRate &&
      form.selectedShippingRateId === this.shipToStoreRate.id

    return (
      <Box paddingVertical={1}>
        <RadioGroup
          name="selectedShippingRateId"
          aria-label="Shipment Method"
          selectedValue={form.selectedShippingRateId}
          onChange={this.updateShippingFormField}
          required={!isShipToStore}
        >
          {shipment.shippingRates
            .filter(
              ({ adminName, shippingMethodId, selected }) =>
                adminName !== 'Store Pickup' &&
                (selected ||
                  this.getShippingMethodById(shippingMethodId)
                    .availableToUsers),
            )
            .map(rate => (
              <RadioGroup.Option key={rate.id} value={rate.id}>
                <Text>
                  {rate.name}: <Price value={Number(rate.cost)} />
                </Text>
              </RadioGroup.Option>
            ))}
        </RadioGroup>
      </Box>
    )
  }

  /**
   * Render helper. Determines how to render the Ship to Store inputs.
   *
   * @returns {function} Component
   */
  renderShipToStoreMethod() {
    if (!this.shipToStoreRate) {
      return false
    }

    const { form, stores } = this.props

    const isShipToStore =
      this.shipToStoreRate &&
      form.selectedShippingRateId === this.shipToStoreRate.id
    const selectedStore = Object.keys(stores)
      .reduce((storeList, state) => [...storeList, ...stores[state]], [])
      .filter(({ id }) => id === form.retailStoreId)[0]

    return (
      <Box paddingVertical={1}>
        <Columns>
          <Columns.Column>
            <Select
              name="retailStoreId"
              value={form.retailStoreId}
              onChange={this.updateShippingFormField}
              required={isShipToStore}
              errorMessages={{
                valueMissing: 'You must select a pickup location',
              }}
              label="Pickup Location"
              options={Object.keys(stores).reduce(
                (grouped, state) => [
                  ...grouped,
                  {
                    label: state
                      .split(' ')
                      .map(word => toTitleCase(word))
                      .join(' '),
                    options: stores[state].map(store => ({
                      label: store.displayName,
                      value: store.id,
                    })),
                  },
                ],
                [],
              )}
            />
          </Columns.Column>
          <Columns.Column>
            <Box minHeight={10}>
              {!!selectedStore && (
                <div>
                  <Heading>Selected Location</Heading>
                  <Address address={selectedStore} />
                  <Text>{selectedStore.shippingTime}</Text>
                  <Text hasEllipsis>
                    <Link
                      href={`/retail_stores/${selectedStore.id}`}
                      target="_blank"
                    >
                      View Store Information
                    </Link>
                  </Text>
                </div>
              )}
            </Box>
          </Columns.Column>
        </Columns>
      </Box>
    )
  }

  /**
   * Determines how the shipment panel should be rendered.
   *
   * @returns {function} Component
   */
  render() {
    const { shipment, order, form, isLoading } = this.props

    const totalQuantity = shipment.manifest.reduce(
      (count, { quantity }) => count + quantity,
      0,
    )

    const isShipToStore =
      this.shipToStoreRate &&
      form.selectedShippingRateId === this.shipToStoreRate.id

    return (
      <Box>
        <Box paddingBottom={1}>
          <Columns isMobile>
            {(shipment.manifest || []).map(({ variant, quantity, lineItemId }) => (
              <Columns.Column
                size={{
                  tiny: 2,
                  small: 1,
                }}
                key={lineItemId}
              >
                {!!(variant && variant.images.length) && (
                  <Image
                    src={variant.images[0].cartUrl}
                    alt={variant.images[0].alt}
                    imageHeight={variant.images[0].attachmentHeight}
                    imageWidth={variant.images[0].attachmentWidth}
                  />
                )}
                {!!(quantity > 1) && (
                  <Text alignment="right" hasEllipsis size="tiny">
                    Qty. {quantity}
                  </Text>
                )}
              </Columns.Column>
            ))}
          </Columns>
        </Box>
        {this.shipToStoreRate ? (
          <Tabs activeTab={Number(isShipToStore)} isUncontrolled>
            <Tabs.TabList tabType="bubbles" isFullwidth>
              <Tabs.Tab
                onSelect={() => this.selectDeliveryType(DELIVERY)}
                isDisabled={isLoading}
              >
                <Clipped>
                  <Text isBold alignment="center" hasEllipsis>
                    Shipping
                  </Text>
                  {!isShipToStore && (
                    <Text size="tiny" alignment="center" hasEllipsis>
                      {`${totalQuantity} Item${(totalQuantity !== 1 && 's') ||
                        ''}`}
                    </Text>
                  )}
                </Clipped>
              </Tabs.Tab>
              <Tabs.Tab
                onSelect={() => this.selectDeliveryType(IN_STORE_PICKUP)}
                isDisabled={isLoading}
              >
                <Clipped>
                  <Box justifyContent="center">
                    <Box width={10}>
                      <Logo name="storePickup" />
                    </Box>
                  </Box>
                  {!!isShipToStore && (
                    <Text size="tiny" alignment="center" hasEllipsis>
                      {`${totalQuantity} Item${(totalQuantity !== 1 && 's') ||
                        ''}`}
                    </Text>
                  )}
                </Clipped>
              </Tabs.Tab>
            </Tabs.TabList>
            <Tabs.Panel>{this.renderShippingMethods()}</Tabs.Panel>
            <Tabs.Panel>{this.renderShipToStoreMethod()}</Tabs.Panel>
          </Tabs>
        ) : (
          this.renderShippingMethods()
        )}
      </Box>
    )
  }
}

ShipmentPanel.propTypes = {
  order: PropTypes.shape({}),
  shipment: PropTypes.shape({
    shippingRates: PropTypes.arrayOf({
      adminName: PropTypes.string,
      shippingMethodId: PropTypes.number,
      selected: PropTypes.bool,
    }),
  }),
  form: PropTypes.shape({}),
  stores: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  onShipmentChange: PropTypes.func,
}

ShipmentPanel.defaultProps = {
  order: {},
  shipment: {},
  form: {},
  stores: [],
  isLoading: false,
  onShipmentChange: () => {},
}

export default withCheckout(ShipmentPanel)
